import type { Airport } from '../game/types';

export interface CityInfo {
  city: string;
  province: string;
  population: string;
  area: string;
  climate: string;
  founded: string;
  highlights: string[];
  history: string;
}

const IATA_TO_CITY: Record<string, string> = {
  PVG: '上海', SHA: '上海',
  PEK: '北京', PKX: '北京',
  CTU: '成都', TFU: '成都',
  SWA: '揭阳',
  LUM: '芒市',
};

export function cityKeyForAirport(airport: Airport): string {
  return IATA_TO_CITY[airport.iata] ?? airport.nameZh;
}

const CITY_DATA: Record<string, CityInfo> = {
  香港: {
    city: '香港', province: '特别行政区', population: '约750万人（2023）', area: '1106 平方公里',
    climate: '亚热带海洋性气候', founded: '1842年开埠',
    highlights: ['维多利亚港', '太平山顶', '中环金融区', '尖沙咀海傍'],
    history: `秦归属番禺县，唐属宝安，明万历时属东莞县。1841年1月英军占领香港岛，1842年《南京条约》割让香港岛；1860年《北京条约》割九龙半岛南端；1898年租借新界99年。1997年7月1日回归祖国设立特别行政区，保留资本主义制度与普通法系，是国际金融、航运、贸易中心之一。`,
  },
  澳门: {
    city: '澳门', province: '特别行政区', population: '约68万人（2023）', area: '33.3 平方公里',
    climate: '亚热带海洋性气候', founded: '1553年葡萄牙人入居',
    highlights: ['大三巴牌坊', '议事亭前地', '路氹金光大道', '妈阁庙'],
    history: `明嘉靖三十二年（1553）葡萄牙人借故登岸居住，万历年间在澳葡人自治。1887年《中葡和好通商条约》承认葡永居管理权。1999年12月20日回归祖国设立特别行政区，是世界最大博彩业中心之一，'东方蒙地卡罗'之称。`,
  },
  北京: {
    city: '北京', province: '直辖市', population: '约2185万人（2023）', area: '16410 平方公里',
    climate: '温带季风气候', founded: '3000余年建城史，800余年建都史',
    highlights: ['故宫', '长城八达岭', '颐和园天坛', '中轴线'],
    history: `公元前11世纪燕国都蓟。辽南京、金中都、元大都、明清京师相继建都于此。1928年国民政府迁都南京改北平，1949年9月定都北京。元大都奠定了今天北京城市格局，老城南北中轴线长达7.8公里，被列入世界遗产预备名录。2023年北京中轴线入选世界文化遗产。`,
  },
  上海: {
    city: '上海', province: '直辖市', population: '约2487万人（2023）', area: '6340.5 平方公里',
    climate: '亚热带季风气候', founded: '唐宋成镇，1291年设县',
    highlights: ['外滩万国建筑', '陆家嘴三高楼', '豫园九曲桥', '石库门里弄'],
    history: `宋咸淳年间（1265-1274）上海镇设立，元至元二十八年（1291）析华亭置上海县。1842年《南京条约》开埠通商，迅速取代广州成为远东最大贸易港。1927年民国政府设上海特别市。新中国成立后保留直辖市，1990年浦东开发开放后跻身全球金融与航运中心。`,
  },
  天津: {
    city: '天津', province: '直辖市', population: '约1363万人（2023）', area: '11966 平方公里',
    climate: '温带季风气候', founded: '1404年设天津卫',
    highlights: ['五大道小洋楼', '意式风情区', '古文化街', '海河夜色'],
    history: `金代称直沽，元延祐三年（1316）设海津镇。明永乐二年（1404）设天津卫，得名'天子津渡'。1860年开埠后辟九国租界。1928年设天津特别市，1949年直辖市。中国近代工业、邮政、大学教育的发祥地之一。`,
  },
  重庆: {
    city: '重庆', province: '直辖市', population: '约3213万人（2023）', area: '82402 平方公里',
    climate: '亚热带季风气候', founded: '公元前316年秦置巴郡',
    highlights: ['洪崖洞吊脚楼', '朝天门两江汇', '磁器口古镇', '红岩革命遗址'],
    history: `周慎靓王五年（前316）秦灭巴国置巴郡，南宋淳熙十六年（1189）升重庆府，得名'双重喜庆'。1891年开埠，抗战时期为国民政府陪都，1938-1945年承载大后方。1997年设直辖市，是西部最大工商业城市与长江上游航运枢纽。`,
  },
  台北: {
    city: '台北', province: '台湾省', population: '约250万人（2023）', area: '271.8 平方公里',
    climate: '亚热带季风气候', founded: '1875年设台北府',
    highlights: ['故宫博物院', '101大楼', '士林夜市', '艋舺龙山寺'],
    history: `清光绪元年（1875）设台北府，1884年台北府城竣工。日据时期（1895-1945）实施台北州市制，台湾总督府设此。1945年台湾光复，1967年升格'直辖市'，是台湾政治、经济、文化中心。`,
  },
  高雄: {
    city: '高雄', province: '台湾省', population: '约273万人（2023）', area: '2946 平方公里',
    climate: '热带季风气候', founded: '明郑时期已开港',
    highlights: ['莲池潭', '爱河夜景', '旗津半岛', '驳二艺术特区'],
    history: `古称打狗（Tá-káo），明郑时期已成渔港，1863年正式开港通商。日据时期改名高雄，1920年设高雄州。1979年升格'直辖市'，二十世纪后期为世界第三大货柜港，台湾重工业与石化业重镇。`,
  },
  深圳: {
    city: '深圳', province: '广东省', population: '约1779万人（2023）', area: '1997 平方公里',
    climate: '亚热带季风气候', founded: '1979年设市',
    highlights: ['莲花山顶', '大鹏所城', '华强北电子', '前海自贸区'],
    history: `秦属番禺，明设深圳墟，清末广九铁路在此设站。1979年3月撤宝安县设深圳市，1980年8月设深圳经济特区，是中国改革开放最早的试验田。四十余年间由边陲渔村跃升为人口近1800万的国际科技与制造业中心。`,
  },
  广州: {
    city: '广州', province: '广东省', population: '约1882万人（2023）', area: '7434 平方公里',
    climate: '亚热带季风气候', founded: '公元前214年筑番禺城',
    highlights: ['陈家祠', '沙面租界', '越秀山镇海楼', '粤菜发源地'],
    history: `秦王政三十三年（前214）设南海郡番禺县，三国吴黄武五年（226）始有'广州'之名。唐宋时为'海上丝绸之路'主港，清'一口通商'时期独揽对外贸易。1921年2月设广州市政厅，是中国第一个建制市。`,
  },
  珠海: {
    city: '珠海', province: '广东省', population: '约247万人（2023）', area: '1736 平方公里',
    climate: '亚热带海洋性气候', founded: '1979年设市',
    highlights: ['情侣路', '外伶仃岛', '圆明新园', '横琴新区'],
    history: `秦属番禺，1953年成立珠海县，1979年3月撤县设市，1980年8月设立珠海经济特区。是中国最早开放的城市之一，毗邻澳门，2009年横琴开发启动，2018年港珠澳大桥通车使珠海成为唯一与港澳陆路相连的特区。`,
  },
  揭阳: {
    city: '揭阳', province: '广东省', population: '约565万人（2023）', area: '5265 平方公里',
    climate: '亚热带季风气候', founded: '公元前214年秦设揭阳县',
    highlights: ['进贤门', '德安里', '普宁古城', '惠来金海湾'],
    history: `秦王政三十三年（前214）属南海郡置揭阳县，因揭岭得名。北宋绍兴十年（1140）复置揭阳县。1991年撤汕头市分治设揭阳市。潮客文化交汇地，'潮汕文化生态保护区'核心，潮汕文化的发祥地之一。`,
  },
  厦门: {
    city: '厦门', province: '福建省', population: '约532万人（2023）', area: '1701 平方公里',
    climate: '亚热带海洋性气候', founded: '1394年明筑厦门城',
    highlights: ['鼓浪屿', '南普陀寺', '环岛路', '集美学村'],
    history: `宋属泉州府同安县。明洪武二十七年（1394）筑厦门城以防倭寇。1655年郑成功改中左所为思明州。1843年五口通商开埠，1980年设厦门经济特区。2017年鼓浪屿被列入世界文化遗产。`,
  },
  福州: {
    city: '福州', province: '福建省', population: '约845万人（2023）', area: '12251 平方公里',
    climate: '亚热带季风气候', founded: '公元前202年汉置冶县',
    highlights: ['三坊七巷', '鼓山涌泉寺', '烟台山租界', '西湖公园'],
    history: `汉高祖五年（前202）无诸建闽越国都冶。唐开元十三年（725）设福州都督府，'福州'始见。宋为六闽之首，明代起长期为福建省会。1946年设福州市。三坊七巷现存明清建筑270余座为'中国城市里坊活化石'。`,
  },
  杭州: {
    city: '杭州', province: '浙江省', population: '约1252万人（2023）', area: '16850 平方公里',
    climate: '亚热带季风气候', founded: '公元前222年秦置钱唐县',
    highlights: ['西湖双堤', '大运河拱宸桥', '灵隐飞来峰', '良渚古城'],
    history: `秦王政二十五年（前222）置钱唐县，隋开皇九年（589）改杭州。五代吴越国、南宋相继建都，南宋杭州为世界最大都会之一。元后仍为浙江政治经济中心。2011年良渚古城列入世界遗产，西湖为大运河沿线最早的运河城市。`,
  },
  宁波: {
    city: '宁波', province: '浙江省', population: '约969万人（2023）', area: '9816 平方公里',
    climate: '亚热带季风气候', founded: '公元前222年秦置句章县',
    highlights: ['天一阁', '河姆渡遗址', '舟山港', '南塘老街'],
    history: `秦王政二十五年（前222）置句章、鄞、鄮三县。唐长庆元年（821）迁州治至三江口建城，明洪武十四年（1381）称宁波府，'海定则波宁'。1844年开埠通商，2018年起舟山港货物吞吐量居世界之首。河姆渡遗址证明7000年前已有稻作文明。`,
  },
  义乌: {
    city: '义乌', province: '浙江省', population: '约190万人（2023）', area: '1105 平方公里',
    climate: '亚热带季风气候', founded: '公元前222年秦置乌伤县',
    highlights: ['国际商贸城', '双林寺', '佛堂古镇', '绣湖公园'],
    history: `秦王政二十五年（前222）置乌伤县，唐武德七年（624）改名义乌，相传秦时颜乌以孝得名，乌助衔土为葬。1988年5月撤县设市。小商品市场由‘鸡毛换糖’起步，发展为全球最大小商品批发市场，被誉为‘世界小商品之都’。`,
  },
  南京: {
    city: '南京', province: '江苏省', population: '约954万人（2023）', area: '6587 平方公里',
    climate: '亚热带季风气候', founded: '公元前333年楚筑金陵邑',
    highlights: ['中山陵', '秦淮河夫子庙', '明城墙', '紫金山天文台'],
    history: `公元前333年楚威王置金陵邑。三国吴黄龙元年（229）孙权建都建业。此后东晋南朝、明初、太平天国、中华民国相继建都于此，号称'六朝古都'。1927年4月国民政府定都南京并制定《首都计划》。`,
  },
  无锡: {
    city: '无锡', province: '江苏省', population: '约749万人（2023）', area: '4628 平方公里',
    climate: '亚热带季风气候', founded: '公元前202年汉置无锡县',
    highlights: ['鼋头渚樱花', '灵山梵宫', '惠山古镇', '梅园'],
    history: `汉高祖五年（前202）置无锡县，相传周平王东迁时锡山产锡而得名'无锡'。1949年设无锡市，1983年辖江阴、宜兴。明清以来为江南米都，民国实业家荣氏家族创办申新纱厂，开中国民族轻工业先河。`,
  },
  郑州: {
    city: '郑州', province: '河南省', population: '约1300万人（2023）', area: '7567 平方公里',
    climate: '温带季风气候', founded: '公元前1600年商都西亳',
    highlights: ['二七纪念塔', '少林寺', '嵩山中岳', '黄帝故里'],
    history: `公元前1600年商汤都西亳（郑州二里岗遗址为商代早期都城），隋开皇元年（581）置郑州。1954年河南省会由开封迁郑州。京广、陇海铁路交会使其成为全国铁路网中心，'火车拉来的省会'。`,
  },
  武汉: {
    city: '武汉', province: '湖北省', population: '约1377万人（2023）', area: '8569 平方公里',
    climate: '亚热带季风气候', founded: '公元223年筑夏口城',
    highlights: ['黄鹤楼', '东湖磨山', '户部巷早点', '武大樱花'],
    history: `东汉末建安年间孙权筑夏口城于黄鹄山。明成化年间汉水改道形成汉口镇，清咸丰八年（1858）开埠通商。1927年国民政府合并武昌、汉口、汉阳三镇设武汉市，1911年辛亥武昌起义于此爆发。`,
  },
  长沙: {
    city: '长沙', province: '湖南省', population: '约1051万人（2023）', area: '11819 平方公里',
    climate: '亚热带季风气候', founded: '公元前222年秦置长沙郡',
    highlights: ['橘子洲头', '岳麓书院', '马王堆汉墓', '开福寺'],
    history: `秦王政二十四年（前222）置长沙郡，汉高祖五年（前202）封吴芮为长沙王。五代马殷建楚国都于此，南宋岳飞在此练水军对抗金。1904年开埠通商，1949年8月和平解放。马王堆汉墓西汉文物举世闻名。`,
  },
  南昌: {
    city: '南昌', province: '江西省', population: '约654万人（2023）', area: '7195 平方公里',
    climate: '亚热带季风气候', founded: '公元前201年灌婴筑城',
    highlights: ['滕王阁', '八一广场', '赣江风光带', '八大山人纪念馆'],
    history: `汉高祖六年（前201）灌婴筑城置南昌县，得名'南方昌盛'。唐代王勃《滕王阁序》传诵至今。1927年8月1日南昌起义打响武装反抗第一枪，故称'英雄城'。1949年江西省会。`,
  },
  合肥: {
    city: '合肥', province: '安徽省', population: '约985万人（2023）', area: '11445 平方公里',
    climate: '亚热带季风气候', founded: '公元前221年秦置合肥县',
    highlights: ['大蜀山', '包公园', '徽园', '科大东区'],
    history: `秦王政二十六年（前221）置合肥县，因东淝河与南淝河交汇而得名。三国时为曹魏扬州治所。1945年抗战胜利后成为安徽省会。1949年设市。2017年合肥综合性国家科学中心获批，为首批三个综合性国家科学中心之一。`,
  },
  沈阳: {
    city: '沈阳', province: '辽宁省', population: '约914万人（2023）', area: '12860 平方公里',
    climate: '温带季风气候', founded: '公元前129年侯城',
    highlights: ['沈阳故宫', '张氏帅府', '北陵东陵', '九一八历史博物馆'],
    history: `西汉建侯城，辽置沈州，元延祐元年（1314）称沈阳。1625年清太祖努尔哈赤迁都于此改名盛京，1636年清朝建立于此故称'盛京'。1948年沈阳解放，新中国成立后为最大重工业基地之一，被誉为'共和国长子'。`,
  },
  大连: {
    city: '大连', province: '辽宁省', population: '约753万人（2023）', area: '12574 平方公里',
    climate: '温带季风气候', founded: '1899年沙俄建达里尼市',
    highlights: ['星海广场', '棒棰岛', '旅顺口', '东港商务区'],
    history: `1899年沙俄租借大连湾建达里尼市，1905年日俄战争后被日本占据改名大连。1945年苏联租借旅大，1955年完全回归。1984年设计划单列市，是中国首批沿海开放城市与东北亚航运中心。`,
  },
  青岛: {
    city: '青岛', province: '山东省', population: '约1037万人（2023）', area: '11293 平方公里',
    climate: '温带季风气候', founded: '1891年清政府设防',
    highlights: ['八大关花石楼', '栈桥回澜阁', '崂山太清宫', '啤酒博物馆'],
    history: `1891年清光绪十七年驻兵设防，被视为青岛建置之始。1897年德国强占胶州湾，1914年被日本取代，1922年华府会议收回主权。1930年改青岛市。'红瓦绿树碧海蓝天'的城市风貌源自德占时期规划。`,
  },
  济南: {
    city: '济南', province: '山东省', population: '约942万人（2023）', area: '10244 平方公里',
    climate: '温带季风气候', founded: '公元前694年齐桓公会盟',
    highlights: ['趵突泉', '大明湖', '千佛山', '洪家楼天主堂'],
    history: `春秋时齐地泺邑，汉初置历城县。汉武帝时设济南郡，'济南'得名于济水之南。北宋政和六年（1116）升济南府。1929年设济南市，是山东省会。城内七十二名泉之冠的趵突泉被誉为'天下第一泉'。`,
  },
  烟台: {
    city: '烟台', province: '山东省', population: '约705万人（2023）', area: '13746 平方公里',
    climate: '温带季风气候', founded: '1398年明设奇山守御千户所',
    highlights: ['蓬莱阁', '长岛九丈崖', '养马岛', '张裕酒文化博物馆'],
    history: `明洪武三十一年（1398）设奇山守御千户所防倭，烽火台称'狼烟墩'，故名烟台。1861年开埠，1871年张弼士创办张裕葡萄酒公司。1983年烟台地区撤地设市，是中国葡萄酒工业发源地。`,
  },
  石家庄: {
    city: '石家庄', province: '河北省', population: '约1122万人（2023）', area: '13504 平方公里',
    climate: '温带季风气候', founded: '1902年京汉铁路设站',
    highlights: ['正定古城', '赵州桥', '西柏坡', '抱犊寨'],
    history: `明嘉靖年间石家庄仅为正定府获鹿县一小村。1902年卢汉铁路设站推动崛起，1925年尝试合并休门建石门市。1947年人民解放军攻克石门改名石家庄，1968年河北省会由保定迁此。西柏坡是中共七届二中全会召开地。`,
  },
  太原: {
    city: '太原', province: '山西省', population: '约543万人（2023）', area: '6988 平方公里',
    climate: '温带季风气候', founded: '公元前497年晋阳城',
    highlights: ['晋祠圣母殿', '双塔寺', '龙山石窟', '汾河公园'],
    history: `周敬王二十三年（前497）赵简子筑晋阳城。秦庄襄王二年（前248）置太原郡。北宋太平兴国四年（979）毁晋阳另筑新城。1927年设太原市，1949年山西省会。是中国重工业基地，煤炭钢铁机械工业重要基地。`,
  },
  呼和浩特: {
    city: '呼和浩特', province: '内蒙古自治区', population: '约358万人（2023）', area: '17224 平方公里',
    climate: '温带大陆性气候', founded: '1581年阿勒坦汗筑归化城',
    highlights: ['大昭寺', '席力图召', '五塔寺', '哈素海'],
    history: `战国赵武灵王置云中郡。明万历九年（1581）阿勒坦汗筑归化城（今旧城），清雍正十三年（1735）于东北筑绥远城。1913年合并称归绥，1954年改名呼和浩特，蒙古语意为'青城'。1947年内蒙古自治区成立时定为首府。`,
  },
  海拉尔: {
    city: '海拉尔', province: '内蒙古自治区', population: '约37万人（2023）', area: '1440 平方公里',
    climate: '温带大陆性气候', founded: '1732年清设呼伦贝尔城',
    highlights: ['成吉思汗广场', '呼伦湖', '莫日格勒河', '敖鲁古雅驯鹿乡'],
    history: `清雍正十年（1732）设呼伦贝尔总管，光绪三十三年（1907）建呼伦贝尔城（海拉尔）。1940年伪满洲国设海拉尔市，得名于海拉尔河（蒙古语意为'冰凉的河'）。1949年成立呼伦贝尔盟治所，2001年升市设海拉尔区。`,
  },
  鄂尔多斯: {
    city: '鄂尔多斯', province: '内蒙古自治区', population: '约219万人（2023）', area: '87000 平方公里',
    climate: '温带大陆性气候', founded: '公元前206年秦属上郡',
    highlights: ['成吉思汗陵', '响沙湾', '库布齐沙漠', '恩格贝'],
    history: `战国时林胡楼烦所居，秦灭六国后入上郡。明洪武年间蒙古鄂尔多斯部驻牧于此。清顺治六年（1649）设鄂尔多斯七旗。2001年东胜市改设鄂尔多斯市。'鄂尔多斯'（Ordos）蒙古语意为'宫帐群'，煤炭储量巨大，素有『羊煤土气』之称。`,
  },
  西安: {
    city: '西安', province: '陕西省', population: '约1300万人（2023）', area: '10752 平方公里',
    climate: '温带季风气候', founded: '公元前11世纪西周镐京',
    highlights: ['秦兵马俑', '大雁塔', '明城墙', '回民街尝鲜'],
    history: `周武王都镐京，秦都咸阳、汉都长安均坐落于今西安近郊。公元前200年汉高祖置长安县，唐天宝年间人口过百万为当时世界最大城市。明洪武七年（1374）建今西安城。西汉唐明13朝古都，1981年兵马俑被誉为'世界第八奇迹'。`,
  },
  兰州: {
    city: '兰州', province: '甘肃省', population: '约442万人（2023）', area: '13086 平方公里',
    climate: '温带半干旱气候', founded: '公元前86年汉置金城县',
    highlights: ['黄河铁桥中山桥', '白塔山', '五泉山', '水车博览园'],
    history: `汉昭帝始元六年（前81）置金城县，得名'金城汤池'。隋开皇元年（581）设兰州总管府，'兰州'初现。1941年设兰州市，1949年甘肃省会。是丝绸之路重镇，黄河自西向东穿城，为中国唯一被黄河穿过的省会。`,
  },
  西宁: {
    city: '西宁', province: '青海省', population: '约247万人（2023）', area: '7660 平方公里',
    climate: '高原大陆性气候', founded: '公元前121年汉置西平亭',
    highlights: ['塔尔寺酥油花', '东关清真大寺', '青海湖', '日月山'],
    history: `汉武帝时霍去病征匈奴置西平亭。北宋崇宁三年（1104）置西宁州，'西陲安宁'。1929年青海建省设西宁市为省会。地处湟水谷地，青藏高原东方门户，是茶马互市的起点城市。`,
  },
  乌鲁木齐: {
    city: '乌鲁木齐', province: '新疆维吾尔自治区', population: '约408万人（2023）', area: '14216 平方公里',
    climate: '温带大陆性气候', founded: '公元前176年西域都护府',
    highlights: ['天山天池', '国际大巴扎', '红山远眺', '南山牧场'],
    history: `汉唐属西域都护府，元称'乌鲁木齐'，蒙古语意为'优美的牧场'。清乾隆二十年（1755）筑乌鲁木齐城，二十八年（1763）赐名迪化。1954年恢复乌鲁木齐原名。是亚洲大陆地理中心，距海最远的大城市。`,
  },
  伊宁: {
    city: '伊宁', province: '新疆维吾尔自治区', population: '约77万人（2023）', area: '789 平方公里',
    climate: '温带大陆性气候', founded: '1762年清设宁远城',
    highlights: ['喀赞其民俗村', '六星街', '伊犁河', '那拉提草原'],
    history: `西汉属乌孙国，唐仙萼州，清乾隆二十七年（1762）设伊犁将军府于惠远城。光绪十四年（1888）设宁远县，民国1914年改为伊宁县。1952年由伊宁县分置伊宁市。伊犁被誉为'塞外江南'。`,
  },
  喀什: {
    city: '喀什', province: '新疆维吾尔自治区', population: '约452万人（2023）', area: '1620 平方公里',
    climate: '温带大陆性气候', founded: '公元前2世纪张骞所见疏勒',
    highlights: ['艾提尕尔清真寺', '老城高台民居', '国际大巴扎', '帕米尔高原'],
    history: `公元前128年张骞通西域所记疏勒国，公元73年东汉设西域都护府管辖。唐贞观二十二年（648）设疏勒都督府，宋代为喀喇汗王朝都城之一。清乾隆二十四年（1759）平定大小和卓之乱设喀什噶尔回城。1952年设喀什市。`,
  },
  拉萨: {
    city: '拉萨', province: '西藏自治区', population: '约87万人（2023）', area: '29640 平方公里',
    climate: '高原温带半干旱气候', founded: '633年松赞干布迁都',
    highlights: ['布达拉宫', '大昭寺', '八廓街转经', '纳木错'],
    history: `633年吐蕃赞普松赞干布迁都于此建宫堡。唐文成公主入藏后建大昭寺，拉萨成为西藏政治宗教中心。1720年清廷设驻藏大臣。1960年设拉萨市。布达拉宫与罗布林卡、大昭寺为联合国教科文组织世界文化遗产。`,
  },
  成都: {
    city: '成都', province: '四川省', population: '约2127万人（2023）', area: '14335 平方公里',
    climate: '亚热带季风气候', founded: '公元前311年张若筑大城',
    highlights: ['武侯祠', '宽窄巷子', '都江堰', '大熊猫繁育基地'],
    history: `约公元前311年秦国张仪、张若筑大城，三国时为蜀汉国都。唐宋时号'扬一益二'，与扬州并称天下最繁盛之地。都江堰水利使成都平原成为'天府之国'。明末清初遭张献忠洗劫，清初'湖广填四川'重塑人口结构。`,
  },
  绵阳: {
    city: '绵阳', province: '四川省', population: '约487万人（2023）', area: '20281 平方公里',
    climate: '亚热带季风气候', founded: '公元前201年汉置涪县',
    highlights: ['越王楼', '富乐山', '九皇山猿王洞', '梓潼大庙山'],
    history: `汉高祖六年（前201）置涪县，西魏废帝二年（553）置巴西郡。隋开皇五年（585）设绵州。1949年设绵阳专区，1985年撤地设市。是中国唯一科技城，国务院批准建设的国家级科技城，『两弹一星』研发基地之一。`,
  },
  宜宾: {
    city: '宜宾', province: '四川省', population: '约459万人（2023）', area: '13283 平方公里',
    climate: '亚热带季风气候', founded: '公元前182年汉置僰道县',
    highlights: ['翠屏山', '李庄古镇', '蜀南竹海', '五粮液工业园'],
    history: `西汉高后六年（前182）置僰道县，梁大同十年（544）改戎州。宋政和四年（1114）改宜宾，『宜宾』取『宜人之宾』意。1949年后设宜宾专区，1996年撤地设市。金沙江与岷江于此汇长江，号称『万里长江第一城』，五粮液产地。`,
  },
  昆明: {
    city: '昆明', province: '云南省', population: '约860万人（2023）', area: '21013 平方公里',
    climate: '亚热带高原季风气候', founded: '公元前279年滇国',
    highlights: ['滇池大观楼', '西山龙门', '翠湖红嘴鸥', '世博园'],
    history: `战国楚顷襄王派庄蹻入滇建国，元至元十三年（1276）设云南行省置昆明县。1915年蔡锷在此誓师护国起义反对袁世凯称帝。1928年设昆明市。终年气候宜人，号称『春城』。`,
  },
  丽江: {
    city: '丽江', province: '云南省', population: '约127万人（2023）', area: '20552 平方公里',
    climate: '亚热带高原气候', founded: '1253年忽必烈设通安州',
    highlights: ['大研古城', '玉龙雪山', '束河茶马古镇', '泸沽湖'],
    history: `战国属滇国，唐南诏铁桥城，南宋宝祐元年（1253）忽必烈征大理设通安州。元至元十三年（1276）设丽江路，明洪武十五年（1382）设丽江府。1997年丽江古城被列入世界文化遗产，为纳西东巴文化与木氏土司史迹集中之地。`,
  },
  西双版纳: {
    city: '西双版纳', province: '云南省', population: '约133万人（2023）', area: '19096 平方公里',
    climate: '热带季风气候', founded: '1570年明设十二版纳',
    highlights: ['中科院植物园', '曼听公园', '野象谷', '望天树空中走廊'],
    history: `西汉属益州郡，唐南诏置茫乃道，宋大理国置勐泐。元至元十一年（1274）设彻里军民总管府。明隆庆四年（1570）车里宣慰司划分十二版纳，『西双版纳』意为十二千田。1953年成立西双版纳傣族自治州，是中国热带生态保护核心。`,
  },
  芒市: {
    city: '芒市', province: '云南省', population: '约44万人（2023）', area: '2901 平方公里',
    climate: '亚热带季风气候', founded: '1932年民国设潞西设治局',
    highlights: ['勐焕大金塔', '勐巴娜西珍奇园', '树包塔', '孔雀湖'],
    history: `元至元十三年（1276）设镇西路军民总管府。1932年民国设潞西设治局，1949年改潞西县。1996年潞西撤县设市。2010年潞西市更名芒市，傣语『勐焕』意为『黎明之城』。地处中缅边境，德宏傣族景颇族自治州首府。`,
  },
  贵阳: {
    city: '贵阳', province: '贵州省', population: '约622万人（2023）', area: '8043 平方公里',
    climate: '亚热带季风气候', founded: '1279年元置顺元城',
    highlights: ['甲秀楼', '黔灵山', '青岩古镇', '花溪十里河滩'],
    history: `元代至元十六年（1279）置顺元城，明洪武四年（1371）设贵州卫。隆庆二年（1568）迁程番府于此改名贵阳，得名『在贵山之阳』。1941年设贵阳市，1949年后为贵州省会。气候凉爽，号称『避暑之都』。`,
  },
  南宁: {
    city: '南宁', province: '广西壮族自治区', population: '约891万人（2023）', area: '22112 平方公里',
    climate: '亚热带季风气候', founded: '318年东晋置晋兴郡',
    highlights: ['青秀山', '南湖公园', '扬美古镇', '中山路美食街'],
    history: `东晋大兴元年（318）置晋兴郡，唐贞观八年（634）改名邕州，元泰定元年（1324）改南宁路得名『南疆安宁』。1907年开埠，1949年后为广西省会。1958年广西壮族自治区成立，首府由桂林迁南宁。东盟博览会永久举办地。`,
  },
  海口: {
    city: '海口', province: '海南省', population: '约297万人（2023）', area: '2304 平方公里',
    climate: '热带海洋性气候', founded: '公元前110年汉置珠崖郡',
    highlights: ['骑楼老街', '雷琼地质公园', '万绿园', '假日海滩'],
    history: `汉武帝元封元年（前110）设珠崖郡。唐宋后为琼州治所。1858年《天津条约》开琼州为通商口岸，1926年设海口市。1988年海南建省，海口成为省会，2018年海南自由贸易港建设启动。`,
  },
  三亚: {
    city: '三亚', province: '海南省', population: '约106万人（2023）', area: '1921 平方公里',
    climate: '热带海洋性气候', founded: '610年隋置临振郡',
    highlights: ['亚龙湾', '天涯海角', '南山海上观音', '蜈支洲岛'],
    history: `隋大业六年（610）置临振郡，唐武德五年（622）改名振州。1984年撤崖县设三亚（县级），1987年升为地级市，是中国最南端地级市。热带滨海旅游资源丰富，被誉为『东方夏威夷』。`,
  },
};

export function cityInfoForAirport(airport: Airport): CityInfo | undefined {
  return CITY_DATA[cityKeyForAirport(airport)];
}